import React, { useState } from 'react';
import {
  VStack,
  Text,
  Heading,
  Box,
  Flex,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  useDisclosure,
} from '@chakra-ui/react';
import theme from '../../../theme.js';
import TextAnimationDisplay from '../../TextAnimationDisplay.jsx';
import TallyMarks from './TallyMarkControl.jsx';
import GearDamageText from './GearDamageText.jsx';
import GearCardButtons from './GearCardButtons.jsx';
import GearAlertDialog from './GearAlertDialog.jsx';
import { useGearActions } from './useGearActions.js';

const GearDisplay = ({ gear: gearItem, isSideWeapon, isEquipment }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [storageAlert, setStorageAlert] = useState(false);
  const [alertName, setAlertName] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  const {
    attackDisabled,
    isConsumed,
    isThrownAway,
    attackMessage,
    attackMessageStyle,
    handleAttack,
    handleStore,
    removeGearAnimation,
  } = useGearActions({ gearItem, isSideWeapon, isEquipment });

  const hasDescription = Boolean(gearItem.description?.trim());

  const openTrashAlert = e => {
    e.stopPropagation();
    setAlertName('Throw Away Gear');
    setAlertMessage(`Throw away ${gearItem.name}?`);
    setStorageAlert(false);
    onOpen();
  };

  const openStoreAlert = e => {
    e.stopPropagation();
    setAlertName('Store Gear');
    setAlertMessage(`Move ${gearItem.name} to storage?`);
    setStorageAlert(true);
    onOpen();
  };

  return (
    <Popover
      trigger="hover"
      openDelay={1000}
      closeDelay={150}
      placement="bottom"
    >
      <PopoverTrigger>
        <Box
          borderWidth="1px"
          borderColor="black"
          borderRadius="md"
          position="relative"
          p={3}
          width={
            gearItem.isOversized
              ? { base: '300px', md: '300px', lg: '350px' }
              : { base: '250px', md: '250px', lg: '300px' }
          }
          align={'center'}
          justifyContent={'center'}
          alignItems={'center'}
          animation={
            isConsumed
              ? 'shake 0.8s ease-in-out 3, fadeAway 0.8s ease-in 1.2s forwards'
              : isThrownAway
                ? 'flingAway 0.8s ease-in 1.2s forwards'
                : 'none'
          }
          sx={{
            '@keyframes shake': {
              '0%, 100%': {
                transform: 'translateX(0)',
              },
              '10%, 30%, 50%, 70%, 90%': {
                transform: 'translateX(-5px)',
              },
              '20%, 40%, 60%, 80%': {
                transform: 'translateX(5px)',
              },
            },
            '@keyframes fadeAway': {
              '0%': {
                opacity: 1,
                transform: 'scale(1)',
              },
              '100%': {
                opacity: 0,
                transform: 'scale(0.8)',
              },
            },
            '@keyframes flingAway': {
              '0%': {
                opacity: 1,
                transform: 'translate(0, 0) rotate(0deg) scale(1)',
              },
              '100%': {
                opacity: 0,
                transform:
                  'translate(1200px, -600px) rotate(720deg) scale(0.3)',
              },
            },
          }}
          onClick={() => {
            if (!attackDisabled) handleAttack();
          }}
          _hover={{
            cursor: 'pointer',
            bg: gearItem.isEquipment
              ? theme.colors.leftSideLight
              : theme.colors.rightSideLight,
          }}
          _active={{
            cursor: 'pointer',
            bg: !attackDisabled
              ? gearItem.isEquipment
                ? theme.colors.leftSideMedium
                : theme.colors.rightSideMedium
              : gearItem.isEquipment
                ? theme.colors.leftSideLight
                : theme.colors.rightSideLight,
          }}
          boxShadow={
            gearItem.isSpecial
              ? `0 0 20px ${theme.colors.luckPointMain}`
              : `0 0 5px black`
          }
        >
          <TextAnimationDisplay
            text={attackMessage}
            textStyle={attackMessageStyle}
            bgColor={
              gearItem.isEquipment
                ? theme.colors.leftSideLight
                : theme.colors.rightSideLight
            }
          />

          {gearItem.totalUses !== null && (
            <Box
              position="absolute"
              left="10px"
              top="50%"
              transform="translateY(-50%)"
            >
              <TallyMarks
                used={gearItem.remainingUses}
                total={gearItem.totalUses}
                verticleOrientation={true}
              />
            </Box>
          )}

          <Flex w="100%" position="relative" justify="center">
            <GearCardButtons
              onTrashClick={openTrashAlert}
              onStoreClick={openStoreAlert}
            />

            <VStack w="100%" align={'center'} spacing={1}>
              <Heading
                size={gearItem.isOversized ? 'xl' : 'md'}
                maxWidth={gearItem.isOversized ? '250px' : '200px'}
                maxHeight="100px"
                overflow="hidden"
                marginBottom="10px"
                textShadow={
                  gearItem.isOversized
                    ? `0 0 5px ${theme.colors.characterDeath}`
                    : `0 0 0px transparent`
                }
              >
                {gearItem.name}
              </Heading>
              <Badge>{gearItem.trait.name}</Badge>
              <GearDamageText gearItem={gearItem} />
            </VStack>
          </Flex>
        </Box>
      </PopoverTrigger>
      {hasDescription && (
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody textAlign="center" fontSize="10pt">
            <Text fontWeight={'semibold'}>{gearItem.description}</Text>
          </PopoverBody>
        </PopoverContent>
      )}
      <GearAlertDialog
        isOpen={isOpen}
        onClose={onClose}
        title={alertName}
        message={alertMessage}
        isEquipment={gearItem.isEquipment}
        onConfirm={
          storageAlert ? handleStore : () => removeGearAnimation(false)
        }
      />
    </Popover>
  );
};

export default GearDisplay;
